import React, { useState, useEffect } from 'react';
import { Users, UserCheck, X, Plus, Search, Edit2, Trash2 } from 'lucide-react';
import { db } from '../../firebase/config';
import { collection, getDocs, doc, updateDoc, query, where, writeBatch } from 'firebase/firestore';

const DivisionAssignmentsWidget = () => {
  const [students, setStudents] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [newDivisionName, setNewDivisionName] = useState('');
  const [editDivisionName, setEditDivisionName] = useState('');
  const [selectedCoordinator, setSelectedCoordinator] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [studentsSnap, coordinatorsSnap] = await Promise.all([
        getDocs(collection(db, 'students')),
        getDocs(query(collection(db, 'users'), where('role', 'in', ['coordinator', 'class_coordinator'])))
      ]);

      const studentsList = studentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudents(studentsList.sort((a, b) => a.rollNumber.localeCompare(b.rollNumber)));

      const coordinatorsList = coordinatorsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCoordinators(coordinatorsList);

      const divisionMap = {};
      studentsList.forEach(student => {
        // Skip students without a valid division assignment
        if (student.division && student.division !== null && student.division !== '') {
          if (!divisionMap[student.division]) {
            divisionMap[student.division] = { name: student.division, students: [], coordinator: null };
          }
          divisionMap[student.division].students.push(student);
        }
      });

      coordinatorsList.forEach(coordinator => {
        if (coordinator.assignedDivision && divisionMap[coordinator.assignedDivision]) {
          divisionMap[coordinator.assignedDivision].coordinator = coordinator;
        }
      });

      setDivisions(Object.values(divisionMap).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const handleSelectRange = () => {
    if (!rangeStart || !rangeEnd) {
      alert('Enter both start and end roll numbers');
      return;
    }

    const inRange = students.filter(s => 
      s.rollNumber >= rangeStart.toUpperCase() && s.rollNumber <= rangeEnd.toUpperCase()
    ).map(s => s.id);

    setSelectedStudents(prev => [...new Set([...prev, ...inRange])]);
    setRangeStart('');
    setRangeEnd('');
  };

  const handleCreateDivision = async () => {
    if (!newDivisionName || selectedStudents.length === 0) {
      alert('Enter division name and select students');
      return;
    }

    setLoading(true);
    try {
      const batch = writeBatch(db);
      selectedStudents.forEach(studentId => {
        batch.update(doc(db, 'students', studentId), { division: newDivisionName });
      });
      await batch.commit();

      alert(`Division ${newDivisionName} created with ${selectedStudents.length} students`);
      setShowCreateModal(false);
      setNewDivisionName('');
      setSelectedStudents([]);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to create division');
    }
    setLoading(false);
  };

  const openEditModal = (division) => {
    setSelectedDivision(division);
    setEditDivisionName(division.name);
    setSelectedStudents(division.students.map(s => s.id));
    setShowEditModal(true);
  };

  const handleEditDivision = async () => {
    if (!editDivisionName) {
      alert('Enter division name');
      return;
    }

    setLoading(true);
    try {
      const batch = writeBatch(db);
      
      // Get the coordinator assigned to this division (if any)
      const divisionCoordinator = coordinators.find(c => c.assignedDivision === selectedDivision.name);
      
      // Remove old students from division and clear their coordinator info
      selectedDivision.students.forEach(student => {
        if (!selectedStudents.includes(student.id)) {
          batch.update(doc(db, 'students', student.id), { 
            division: null,
            coordinatorName: null,
            coordinatorEmail: null,
            ccName: null  // Added ccName clearing
          });
        }
      });

      // Update selected students with division name
      selectedStudents.forEach(studentId => {
        const updateData = { division: editDivisionName };
        
        // If division has a coordinator, add coordinator info to new students
        if (divisionCoordinator) {
          updateData.coordinatorName = divisionCoordinator.name;
          updateData.coordinatorEmail = divisionCoordinator.email;
          updateData.ccName = divisionCoordinator.name;  // Added ccName
        }
        
        batch.update(doc(db, 'students', studentId), updateData);
      });

      await batch.commit();

      alert('Division updated successfully');
      setShowEditModal(false);
      setSelectedStudents([]);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to update division');
    }
    setLoading(false);
  };

  const handleDeleteDivision = async (division) => {
    if (!window.confirm(`Delete division ${division.name}? All students will be unassigned.`)) return;

    setLoading(true);
    try {
      const batchOp = writeBatch(db);

      // Unassign all students and clear coordinator information
      division.students.forEach(student => {
        batchOp.update(doc(db, 'students', student.id), { 
          division: null,
          coordinatorName: null,
          coordinatorEmail: null,
          ccName: null  // Added ccName clearing
        });
      });

      // Unassign coordinator
      if (division.coordinator) {
        batchOp.update(doc(db, 'users', division.coordinator.id), { assignedDivision: null });
      }

      await batchOp.commit();
      alert('Division deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to delete division');
    }
    setLoading(false);
  };

  const handleAssignCoordinator = async () => {
    if (!selectedCoordinator) {
      alert('Select a coordinator');
      return;
    }

    setLoading(true);
    try {
      const batchOp = writeBatch(db);
      const selectedCoordinatorData = coordinators.find(c => c.id === selectedCoordinator);
      
      // Unassign old coordinator if exists
      const oldCoordinator = coordinators.find(c => c.assignedDivision === selectedDivision.name);
      if (oldCoordinator) {
        batchOp.update(doc(db, 'users', oldCoordinator.id), { assignedDivision: null });
        
        // Remove coordinatorName, coordinatorEmail, and ccName from students of old coordinator
        selectedDivision.students.forEach(student => {
          batchOp.update(doc(db, 'students', student.id), { 
            coordinatorName: null,
            coordinatorEmail: null,
            ccName: null  // Added ccName clearing
          });
        });
      }

      // Assign new coordinator to users collection
      batchOp.update(doc(db, 'users', selectedCoordinator), { 
        assignedDivision: selectedDivision.name 
      });

      // Update all students in the division with coordinator information
      selectedDivision.students.forEach(student => {
        batchOp.update(doc(db, 'students', student.id), { 
          coordinatorName: selectedCoordinatorData.name,
          coordinatorEmail: selectedCoordinatorData.email,
          ccName: selectedCoordinatorData.name  // Added ccName field
        });
      });

      // Update or create divisionAssignments document
      const divisionAssignmentRef = doc(db, 'divisionAssignments', selectedDivision.name);
      batchOp.set(divisionAssignmentRef, {
        division: selectedDivision.name,
        coordinatorName: selectedCoordinatorData.name,
        coordinatorEmail: selectedCoordinatorData.email,
        ccName: selectedCoordinatorData.name,  // Added ccName to division assignments
        updatedAt: new Date().toISOString(),
        updatedBy: selectedCoordinatorData.email
      }, { merge: true });

      await batchOp.commit();

      alert('Coordinator assigned successfully');
      setShowAssignModal(false);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to assign coordinator');
    }
    setLoading(false);
  };

  const handleSelectAll = () => {
    setSelectedStudents(filteredStudents.map(s => s.id));
  };

  const handleUnselectAll = () => {
    setSelectedStudents([]);
  };

  const filteredStudents = students.filter(s =>
    s.rollNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${s.firstName} ${s.surname}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold">Division Assignments</h2>
              <p className="text-gray-600 text-sm">Manage student divisions and coordinators</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus className="w-5 h-5" />
            <span>Create Division</span>
          </button>
        </div>

        <div className="space-y-4">
          {divisions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>No divisions created yet</p>
            </div>
          ) : (
            divisions.map(division => (
              <div key={division.name} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold">Division {division.name}</h3>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {division.students.length} Students
                      </span>
                    </div>
                    
                    {division.coordinator ? (
                      <div className="flex items-center space-x-2 text-green-700 bg-green-50 p-2 rounded">
                        <UserCheck className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Coordinator: {division.coordinator.name}
                        </span>
                      </div>
                    ) : (
                      <div className="text-orange-600 text-sm">
                        ⚠ No coordinator assigned
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(division)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit Division"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedDivision(division);
                        setSelectedCoordinator(division.coordinator?.id || '');
                        setShowAssignModal(true);
                      }}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      {division.coordinator ? 'Change' : 'Assign'}
                    </button>
                    <button
                      onClick={() => handleDeleteDivision(division)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Delete Division"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create/Edit Modal (shared logic) */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">{showEditModal ? 'Edit' : 'Create'} Division</h3>
                <button onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedStudents([]);
                }}>
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Division Name</label>
                <input
                  type="text"
                  value={showEditModal ? editDivisionName : newDivisionName}
                  onChange={(e) => showEditModal ? setEditDivisionName(e.target.value.toUpperCase()) : setNewDivisionName(e.target.value.toUpperCase())}
                  placeholder="e.g., A, B, C"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium mb-2">Select by Range</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={rangeStart}
                    onChange={(e) => setRangeStart(e.target.value)}
                    placeholder="From (e.g., FB101)"
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    value={rangeEnd}
                    onChange={(e) => setRangeEnd(e.target.value)}
                    placeholder="To (e.g., FB150)"
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                  <button
                    onClick={handleSelectRange}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Select
                  </button>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search students..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                />
              </div>

              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {selectedStudents.length} students selected
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleUnselectAll}
                    className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Unselect All
                  </button>
                </div>
              </div>

              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {filteredStudents.map(student => (
                  <label
                    key={student.id}
                    className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => handleSelectStudent(student.id)}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium">{student.rollNumber}</p>
                      <p className="text-sm text-gray-600">{student.firstName} {student.surname}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="sticky bottom-0 bg-white p-6 border-t flex space-x-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedStudents([]);
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={showEditModal ? handleEditDivision : handleCreateDivision}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : showEditModal ? 'Update Division' : 'Create Division'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Coordinator Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Assign Coordinator - Division {selectedDivision?.name}</h3>
              <button onClick={() => setShowAssignModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm">
              <p><strong>Students:</strong> {selectedDivision?.students.length}</p>
              {selectedDivision?.coordinator && (
                <p><strong>Current:</strong> {selectedDivision.coordinator.name}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Select Coordinator</label>
              <select
                value={selectedCoordinator}
                onChange={(e) => setSelectedCoordinator(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="">-- Select Coordinator --</option>
                {coordinators.map(coordinator => (
                  <option key={coordinator.id} value={coordinator.id}>
                    {coordinator.name} {coordinator.assignedDivision && `(Division ${coordinator.assignedDivision})`}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignCoordinator}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DivisionAssignmentsWidget;